import { Fragment } from 'react';
import { Dialog, Transition, Switch } from '@headlessui/react';
import { useTranslation } from 'react-i18next';
import { useDashboardStore } from './useDashboardStore';
import { NAV_ITEMS } from './Sidebar';
import { cn } from '@/lib/cn';
import { XMarkIcon, ArrowPathIcon } from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';

export default function CustomizePanel() {
  const { t } = useTranslation();
  const open = useDashboardStore((s) => s.customizeOpen);
  const setOpen = useDashboardStore((s) => s.setCustomizeOpen);
  const widgets = useDashboardStore((s) => s.widgets);
  const setVisible = useDashboardStore((s) => s.setWidgetVisible);
  const resetWidgets = useDashboardStore((s) => s.resetWidgets);
  const shortcuts = useDashboardStore((s) => s.shortcuts);
  const toggleShortcut = useDashboardStore((s) => s.toggleShortcut);
  const resetShortcuts = useDashboardStore((s) => s.resetShortcuts);

  const shortcutable = NAV_ITEMS.filter((item) => item.path !== '/');

  const handleClose = () => {
    setOpen(false);
    toast.success('Dashboard saved', {
      style: {
        background: '#ffffff',
        color: '#111827',
        border: '1px solid #e5e7eb',
        fontSize: '13px',
      },
      iconTheme: { primary: '#4f46e5', secondary: '#fff' },
    });
  };

  const handleReset = () => {
    resetWidgets();
    resetShortcuts();
    toast('Dashboard reset to default', {
      icon: '↩',
      style: {
        background: '#ffffff',
        color: '#111827',
        border: '1px solid #e5e7eb',
        fontSize: '13px',
      },
    });
  };

  return (
    <Transition show={open} as={Fragment}>
      <Dialog onClose={handleClose} className="relative z-50">
        {/* Backdrop */}
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-200"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-150"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/30 backdrop-blur-sm" aria-hidden="true" />
        </Transition.Child>

        {/* Panel */}
        <div className="fixed inset-0 flex items-center justify-center p-4">
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-200"
            enterFrom="opacity-0 scale-95"
            enterTo="opacity-100 scale-100"
            leave="ease-in duration-150"
            leaveFrom="opacity-100 scale-100"
            leaveTo="opacity-0 scale-95"
          >
            <Dialog.Panel
              className={cn(
                'w-full max-w-md max-h-[85vh] flex flex-col rounded-xl border border-gray-200 bg-white shadow-xl',
                'transition-all',
              )}
            >
              {/* Header */}
              <div className="flex items-center justify-between border-b border-gray-200 px-5 py-4 shrink-0">
                <Dialog.Title className="text-base font-semibold text-gray-900">
                  Customize Dashboard
                </Dialog.Title>
                <button
                  onClick={handleClose}
                  className={cn(
                    'flex h-8 w-8 items-center justify-center rounded-lg',
                    'text-gray-400 hover:text-gray-900 hover:bg-gray-100',
                    'transition-all duration-200 ease-in-out',
                    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500',
                  )}
                  aria-label="Close"
                >
                  <XMarkIcon className="h-5 w-5" />
                </button>
              </div>

              <div className="overflow-y-auto">
              {/* Widget toggles */}
              <div className="px-5">
                <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider pt-4 pb-2">
                  {t('dashboard.widgets', 'Widgets')}
                </p>
                <div className="divide-y divide-gray-100">
                  {widgets.map((w) => (
                    <div
                      key={w.id}
                      className="flex items-center justify-between py-3.5"
                    >
                      <span className="text-sm text-gray-700">{w.label}</span>
                      <Switch
                        checked={w.visible}
                        onChange={(checked: boolean) => setVisible(w.id, checked)}
                        className={cn(
                          'relative inline-flex h-5 w-9 items-center rounded-full',
                          'transition-all duration-200 ease-in-out',
                          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-2 focus-visible:ring-offset-white',
                          w.visible ? 'bg-indigo-600' : 'bg-gray-300',
                        )}
                        aria-label={`Toggle ${w.label}`}
                      >
                        <span
                          className={cn(
                            'inline-block h-3.5 w-3.5 rounded-full bg-white shadow-sm',
                            'transition-transform duration-200 ease-in-out',
                            w.visible ? 'translate-x-4' : 'translate-x-1',
                          )}
                        />
                      </Switch>
                    </div>
                  ))}
                </div>
              </div>

              {/* Quick Actions toggles */}
              <div className="px-5">
                <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider pt-4 pb-2">
                  {t('dashboard.quickActions', 'Quick Actions')}
                </p>
                <div className="divide-y divide-gray-100">
                  {shortcutable.map((item) => {
                    const active = shortcuts.includes(item.path);
                    return (
                      <div
                        key={item.path}
                        className="flex items-center justify-between py-3.5"
                      >
                        <span className="text-sm text-gray-700">{t(item.label)}</span>
                        <Switch
                          checked={active}
                          onChange={() => toggleShortcut(item.path)}
                          className={cn(
                            'relative inline-flex h-5 w-9 items-center rounded-full',
                            'transition-all duration-200 ease-in-out',
                            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-2 focus-visible:ring-offset-white',
                            active ? 'bg-indigo-600' : 'bg-gray-300',
                          )}
                          aria-label={`Toggle ${t(item.label)}`}
                        >
                          <span
                            className={cn(
                              'inline-block h-3.5 w-3.5 rounded-full bg-white shadow-sm',
                              'transition-transform duration-200 ease-in-out',
                              active ? 'translate-x-4' : 'translate-x-1',
                            )}
                          />
                        </Switch>
                      </div>
                    );
                  })}
                </div>
              </div>
              </div>

              {/* Footer */}
              <div className="flex items-center justify-between border-t border-gray-200 px-5 py-4 shrink-0">
                <button
                  onClick={handleReset}
                  className={cn(
                    'flex items-center gap-1.5 text-xs font-medium text-gray-500 hover:text-gray-900',
                    'transition-all duration-200 ease-in-out',
                    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 rounded-md px-2 py-1',
                  )}
                >
                  <ArrowPathIcon className="h-3.5 w-3.5" />
                  Reset defaults
                </button>
                <button
                  onClick={handleClose}
                  className={cn(
                    'rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white',
                    'hover:bg-indigo-500',
                    'transition-all duration-200 ease-in-out',
                    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-2 focus-visible:ring-offset-white',
                  )}
                >
                  Done
                </button>
              </div>
            </Dialog.Panel>
          </Transition.Child>
        </div>
      </Dialog>
    </Transition>
  );
}
